import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Keyboard, HelpCircle, BookOpen, ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { JoinTrail } from "@/components/JoinTrail";
import { api, asset } from "@/lib/api";
import { useSiteConfig } from "@/context/SiteConfig";
import { IMAGES } from "@/data/content";
import { guideIcon } from "@/lib/guideIcons";

export default function PlayerGuide() {
  useTitle("Player Guide | Aurora Springs RP | RedM Roleplay", "New to Aurora Springs RP? Our player guide covers getting started, horse care, camping and frontier survival on our 1895 RedM roleplay server.");
  const { img } = useSiteConfig();
  const [guides, setGuides] = useState([]);
  const [keybinds, setKeybinds] = useState([]);
  const [faq, setFaq] = useState([]);

  useEffect(() => {
    api.get("/guides").then(({ data }) => setGuides(data || [])).catch(() => {});
    api.get("/keybinds").then(({ data }) => setKeybinds(data)).catch(() => {});
    api.get("/faq").then(({ data }) => setFaq(data)).catch(() => {});
  }, []);

  return (
    <>
      <PageHeader overline="New to the frontier?" title="Player Guide" image={img("guide", IMAGES.manifesto)}
        sub="Everything you need to ride into Aurora Springs — from your first connection to the keys that keep you alive." />

      <section className="mx-auto max-w-6xl px-6 sm:px-10 py-20 space-y-24">
        {/* How to Join — interactive trail */}
        <JoinTrail />

        {/* Guides */}
        {guides.length > 0 && (
          <div>
            <Reveal><div className="flex items-center gap-3"><BookOpen className="h-5 w-5 text-teal" /><Overline>Guides &amp; How-Tos</Overline></div></Reveal>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {guides.map((g, i) => {
                const Icon = guideIcon(g.icon);
                return (
                  <Reveal key={g.id || i} delay={(i % 3) * 0.06}>
                    <Link to={`/guide/${g.slug || g.id}`} data-testid={`guide-card-${g.slug || g.id}`}
                      className="group relative flex flex-col h-full border border-[#2A201A] bg-surface/30 hover:border-teal/50 transition-colors overflow-hidden">
                      <div className="relative h-40 overflow-hidden">
                        {g.cover ? (
                          <img src={asset(g.cover)} alt="" className="h-full w-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
                        ) : (
                          <div className="h-full w-full bg-gradient-to-br from-surface to-obsidian" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-obsidian to-transparent" />
                        <Icon className="absolute bottom-4 left-5 h-7 w-7 text-teal" />
                      </div>
                      <div className="flex flex-col flex-1 p-6">
                        <h3 className="font-display text-2xl font-light group-hover:text-teal transition-colors">{g.title}</h3>
                        {g.blurb && <p className="mt-3 text-sm text-[#8F877C] leading-relaxed flex-1">{g.blurb}</p>}
                        <span className="mt-6 inline-flex items-center gap-1.5 font-feature text-xs uppercase tracking-widest text-teal">
                          Read guide <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        )}

        {/* Keybinds */}
        <div>
          <Reveal><div className="flex items-center gap-3"><Keyboard className="h-5 w-5 text-teal" /><Overline>Keybinds &amp; Controls</Overline></div></Reveal>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {keybinds.map((k, i) => (
              <Reveal key={k.id || i} delay={(i % 2) * 0.05}>
                <div className="flex items-center gap-4 border border-[#2A201A] bg-surface/40 p-4" data-testid={`guide-keybind-${i}`}>
                  <kbd className="font-mono text-sm bg-obsidian border border-teal/40 text-teal px-3 py-1.5 min-w-[3rem] text-center">{k.key}</kbd>
                  <span className="text-[#b8b0a4]">{k.action}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <Reveal><div className="flex items-center gap-3"><HelpCircle className="h-5 w-5 text-teal" /><Overline>New Player FAQ</Overline></div></Reveal>
          <div className="mt-8 max-w-3xl">
            <Accordion type="single" collapsible className="space-y-3">
              {faq.map((f, i) => (
                <AccordionItem key={f.id || i} value={String(i)} data-testid={`guide-faq-${i}`}
                  className="border border-[#2A201A] bg-surface/40 px-6 data-[state=open]:border-teal/40 transition-colors">
                  <AccordionTrigger className="hover:no-underline py-5 text-left font-display text-xl font-light">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-[#b8b0a4] leading-relaxed pb-5">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </>
  );
}
